import { useEffect, useState } from 'react';
import { instance } from '../../../axios/AxiosInstance';
import { BOOK_LANGUAGE_CODES } from '../../../utils/globals';
import type { BookLanguageRow, ListResponse } from './singleTableListSupport';
import { settingsListApiError } from './singleTableListSupport';

export const BOOK_LANGUAGE_FORM_EN_ID = 'en';

const allowedLanguageIdSet = new Set<string>(BOOK_LANGUAGE_CODES);

/** Book languages allowed for category/author name keys, sorted; ensures `en` exists for forms. */
export function buildDisplayLanguages(fetched: BookLanguageRow[]): BookLanguageRow[] {
  const filtered = fetched.filter((r) => allowedLanguageIdSet.has(r.id));
  const byId = new Map(filtered.map((r) => [r.id, r]));
  if (!byId.has(BOOK_LANGUAGE_FORM_EN_ID)) {
    byId.set(BOOK_LANGUAGE_FORM_EN_ID, {
      id: BOOK_LANGUAGE_FORM_EN_ID,
      code: BOOK_LANGUAGE_FORM_EN_ID,
      name: 'English',
      updated_at: '',
    });
  }
  const merged = Array.from(byId.values());
  merged.sort((a, b) => {
    if (a.id === BOOK_LANGUAGE_FORM_EN_ID) return -1;
    if (b.id === BOOK_LANGUAGE_FORM_EN_ID) return 1;
    return a.name.localeCompare(b.name, 'mn');
  });
  return merged;
}

/** Loads book languages from `GET /book-language` when `enabled` (e.g. settings/author dialog open). */
export function useBookLanguagesForForm(enabled: boolean) {
  const [rows, setRows] = useState<BookLanguageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** False until the first fetch for this `enabled` session finishes (avoids one-frame empty language list). */
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setLoading(false);
      setError(null);
      setReady(false);
      return;
    }

    setLoading(true);
    setError(null);
    setReady(false);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await instance.get<ListResponse>('book-language');
        const list = Array.isArray(data.data) ? (data.data as BookLanguageRow[]) : [];
        if (!cancelled) setRows(list);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(settingsListApiError(e, 'Хэлийн жагсаалт ачааллахад алдаа гарлаа.'));
          setRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const showLoading = enabled && !ready;
  return { rows, loading: showLoading || loading, error };
}
